/**
 * API Interceptor for handling subscription-related HTTP responses
 *
 * Automatically detects 402 Payment Required responses and can trigger
 * upgrade modals or other subscription-related UI.
 */

let upgradeModalCallback = null;

/**
 * Register a callback to open the upgrade modal
 * This should be called from a component that manages the UpgradeModal state
 *
 * @param {Function} callback - Function to call when upgrade is needed
 */
export function registerUpgradeModalCallback(callback) {
  upgradeModalCallback = callback;
}

/**
 * Enhanced fetch wrapper that handles 402 Payment Required responses
 *
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>} The fetch response
 */
export async function fetchWithSubscriptionCheck(url, options = {}) {
  const response = await fetch(url, options);

  // Handle 402 Payment Required
  if (response.status === 402) {
    console.warn('🔒 Payment required - subscription needed');

    // Try to parse the response for feature information
    let feature = null;
    try {
      const data = await response.clone().json();
      feature = data.feature || data.message || 'deze functie';
    } catch (e) {
      // Response might not be JSON
      feature = 'deze functie';
    }

    // Trigger upgrade modal if callback is registered
    if (upgradeModalCallback) {
      upgradeModalCallback(feature);
    }

    // Return the response anyway so caller can handle it
    return response;
  }

  // Check for trial warning headers
  const trialWarning = response.headers.get('X-Trial-Warning');
  const trialDaysRemaining = response.headers.get('X-Trial-Days-Remaining');

  if (trialWarning === 'true' && trialDaysRemaining) {
    console.info(`⏰ Trial ending soon: ${trialDaysRemaining} days remaining`);
  }

  return response;
}

/**
 * Helper to extract subscription information from response headers
 *
 * @param {Response} response - The fetch response
 * @returns {Object} Subscription info from headers
 */
export function extractSubscriptionInfo(response) {
  return {
    trialDaysRemaining: response.headers.get('X-Trial-Days-Remaining'),
    trialEnd: response.headers.get('X-Trial-End'),
    trialWarning: response.headers.get('X-Trial-Warning') === 'true',
    requiresSubscription: response.status === 402,
  };
}
